// TripCard — Phase 12 Plan 12-03 (UIR-04, telegram-ui migration; R1 + R2).
//
// Native telegram-ui <Cell> row in TripsList:
//   - route "From → To" as the cell header (UI-SPEC §7);
//   - single status pill (R1) styled via TRIP_STATUS_COLORS (no hardcode-hex);
//     the color is ALSO duplicated by the status text label (a11y, UI-SPEC §5 —
//     color is never the sole carrier of meaning);
//   - predictive-ETA string "19 фев, 14:00–18:00 (±2ч)" (ETA-04) + segment
//     progress "3/7 точек" (R2) under the route.
//
// Tap navigates to /dispatcher/trips/:id (TripDetails).
//
// NOTE: the list payload (TripListItem) currently exposes status/driver/ids only
// — route endpoints, ETA window and stop counts are NOT in it yet. We render the
// R1/R2 affordances with graceful "—" fallbacks; a follow-up plan extends the
// list endpoint to populate them (tracked in 12-03-SUMMARY Known Stubs).

import { Cell } from "@telegram-apps/telegram-ui";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { TripListItem } from "../api/trips";
import { DELAY_OVERLAY } from "../lib/tokens";
import { delayOverlayFill, tripStatusColorKey, tripStatusFill } from "../lib/tripStatusBadge";

export interface TripCardProps {
  trip: TripListItem;
  /**
   * R1 delay overlay (UI-SPEC §5). When true, a gold dot is layered ON TOP of the
   * base status pill — it does NOT change the base status color. A delayed trip
   * keeps its green/yellow/red/gray pill AND gains the gold delay marker.
   * Sourced from the predictive-ETA window vs. plan once the list payload carries
   * ETA; defaults to false until then.
   */
  isDelayed?: boolean;
}

/** Optional route/eta/segment fields a richer list payload may carry later. */
interface TripRouteExtras {
  origin_label?: string | null;
  destination_label?: string | null;
  eta_label?: string | null;
  stops_total?: number | null;
  stops_done?: number | null;
}

export function TripCard({ trip, isDelayed = false }: TripCardProps): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const colorKey = tripStatusColorKey(trip);
  const fill = tripStatusFill(colorKey);
  const goldFill = delayOverlayFill();
  const statusLabel = t(`dispatcher:trips.status.${trip.status}`, trip.status) as string;
  const delayLabel = t("dispatcher:trips.status.delayed", "Delayed") as string;

  // Route "From → To". Falls back to external_id / Trip #id until the list
  // endpoint carries origin/destination labels.
  const extras = trip as TripListItem & TripRouteExtras;
  const tripRef =
    trip.external_id ??
    (t("dispatcher:trips.row.no_external_id", "Trip #{{id}}", { id: trip.id }) as string);
  const routeLabel =
    extras.origin_label && extras.destination_label
      ? `${extras.origin_label} → ${extras.destination_label}`
      : tripRef;

  // R2 — predictive ETA window ("19 фев, 14:00–18:00 (±2ч)") + segment progress.
  const etaLabel = extras.eta_label ?? (t("dispatcher:trips.row.eta_unknown", "ETA: —") as string);
  const stopsTotal = extras.stops_total ?? null;
  const stopsDone = extras.stops_done ?? null;
  const progressLabel =
    stopsTotal != null
      ? (t("dispatcher:trips.row.progress", "{{done}}/{{total}} stops", {
          done: stopsDone ?? 0,
          total: stopsTotal,
        }) as string)
      : (t("dispatcher:trips.row.progress_unknown", "— stops") as string);

  const driverLabel =
    trip.driver_id !== null
      ? (t("dispatcher:trips.row.driver", "Driver #{{id}}", { id: trip.driver_id }) as string)
      : (t("dispatcher:trips.row.no_driver", "Unassigned") as string);

  // Single-color status pill (R1). Color duplicated by `statusLabel` text
  // (a11y) and `data-color` reflects the FSM key for tests / colorblind tooling.
  //
  // R1 delay overlay: a delay is a SEPARATE gold layer. The pill keeps its base
  // status color; when `isDelayed`, we render a gold corner-dot ON TOP (positioned
  // relative to the pill) plus a gold "delayed" text chip so the signal is never
  // color-only (a11y / colorblind). `data-delayed` reflects the overlay for tests.
  const statusPill = (
    <span
      data-testid={`trip-card-status-${trip.id}`}
      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
    >
      <span
        data-testid={`trip-card-badge-${trip.id}`}
        data-color={colorKey}
        data-delayed={isDelayed ? "true" : "false"}
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "2px 8px",
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 600,
          color: "#fff",
          backgroundColor: fill,
          whiteSpace: "nowrap",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 6,
            height: 6,
            borderRadius: 3,
            backgroundColor: "#fff",
            opacity: 0.9,
          }}
        />
        {statusLabel}
        {isDelayed && (
          // Gold corner-dot — layered on top of the base-color pill (R1).
          <span
            data-testid={`trip-card-delay-dot-${trip.id}`}
            aria-hidden="true"
            style={{
              position: "absolute",
              top: -2,
              right: -2,
              width: 10,
              height: 10,
              borderRadius: 5,
              backgroundColor: goldFill,
              border: `2px solid ${DELAY_OVERLAY.outline}`,
              boxSizing: "border-box",
            }}
          />
        )}
      </span>
      {isDelayed && (
        // Text marker so the delay is not color-only (a11y, UI-SPEC §5).
        <span
          data-testid={`trip-card-delay-label-${trip.id}`}
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: goldFill,
            whiteSpace: "nowrap",
          }}
        >
          {delayLabel}
        </span>
      )}
    </span>
  );

  return (
    <Cell
      Component="button"
      type="button"
      data-testid={`trip-card-${trip.id}`}
      onClick={() => navigate(`/dispatcher/trips/${trip.id}`)}
      after={statusPill}
      subtitle={
        <span data-testid={`trip-card-eta-${trip.id}`}>
          {etaLabel}
          {" · "}
          <span data-testid={`trip-card-progress-${trip.id}`}>{progressLabel}</span>
        </span>
      }
      description={driverLabel}
      multiline
    >
      <span data-testid={`trip-card-route-${trip.id}`}>{routeLabel}</span>
    </Cell>
  );
}
